import { useState } from "react";

const entryWidth = 25;

/** UI for editing & displaying assignments from keystrokes to one or more symbols */
export function AssignKeys(props: {
  readonly assignmentMap: Record<string, string[]>,
  readonly setAssignment: (keystroke: string, symbols: string[]) => void,
  readonly moveKey: (keystroke: string, where: 'up' | 'down') => void,
  readonly deleteKey: (keystroke: string) => void,
}) {
  const assigned = props.assignmentMap;
  const all = Object.getOwnPropertyNames(assigned);

  function isKeyUsed(keystroke: string): boolean {
    return props.assignmentMap[keystroke] !== undefined;
  }

  return (<>
    <div style={{ display: "flex", justifyContent: "center" }}>
      <table>
        <thead>
          <tr>
            <th>key</th>
            <th>symbol(s)</th>
          </tr>
        </thead>
        <tbody>
          {all.map(keystroke =>
            <AssignmentRow key={genKey(assigned, keystroke)} keystroke={keystroke} symbols={assigned[keystroke]}
              changeAssignment={props.setAssignment} moveKey={props.moveKey} deleteKey={props.deleteKey} />)}
          <AddKeyRow setAssignment={props.setAssignment} isKeyUsed={isKeyUsed} />
        </tbody>
      </table>
    </div>
  </>);
}

/** uniquely identity the values in this row so that the UI is properly updated when things are deleted or changed elsewhere */
function genKey(assigned: Record<string, string[]>, keystroke: string) {
  const gen = `${keystroke}-${assigned[keystroke]}`
  return gen;
}

const sep = ' ';  /** separates symbols */

/** show a keystroke & let them change the symbols attached to it */
function AssignmentRow(props: {
  keystroke: string,
  symbols: string[],
  changeAssignment: (keystroke: string, symbols: string[]) => void,
  moveKey: (keystroke: string, where: 'up' | 'down') => void,
  deleteKey: (keystroke: string) => void,
}) {
  const [symbols, setSymbols] = useState(props.symbols.join(sep));
  const [canChange, setCanChange] = useState(false);

  function changeSymbols(str: string) {
    setSymbols(str);
    setCanChange(true);
  }

  function nowUse() {
    const list = symbols.split(sep).filter(s => s !== '');
    props.changeAssignment(props.keystroke, list);
    setCanChange(false);
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.shiftKey && e.key === 'ArrowUp') {
      props.moveKey(props.keystroke, 'up');
    } else if (e.shiftKey && e.key === 'ArrowDown') {
      props.moveKey(props.keystroke, 'down');
    }
  }

  const keystroke = props.keystroke === ' ' ? '<SP>' : props.keystroke;
  return (
    <tr>
      <th>{keystroke}</th>
      <td>
        <input size={entryWidth} spellCheck={false} value={symbols}
          onChange={(e) => changeSymbols(e.target.value)} onKeyDown={onKeyDown} />
        &nbsp;
        <button onClick={() => nowUse()} disabled={!canChange}>assign</button>
        &nbsp;
        <button onClick={() => props.deleteKey(props.keystroke)}>delete</button>
      </td>
    </tr>
  );
}

/** let them specify a key & symbols to attach to it */
function AddKeyRow(props: {
  readonly setAssignment: (keystroke: string, symbols: string[]) => void,
  readonly isKeyUsed: (keystroke: string) => boolean,
}) {
  const [keystroke, setKeystroke] = useState('');
  const [symbols, setSymbols] = useState<string>('');
  const [canUse, setCanUse] = useState(false);

  function changeKeystroke(key: string) {
    setKeystroke(key);
    setCanUse(key !== '' && !props.isKeyUsed(key));
  }

  function changeSymbols(str: string) {
    setSymbols(str);
    setCanUse(keystroke !== '' && !props.isKeyUsed(keystroke));
  }

  function addAssignment() {
    if (!props.isKeyUsed(keystroke) && keystroke !== '' && symbols.length > 0) {
      const list = symbols.split(sep).filter(s => s !== '');
      props.setAssignment(keystroke, list);
      setKeystroke('');
      setSymbols('');
      setCanUse(false);
      document.getElementById('symbols')?.focus();
    }
  }

  const assign = keystroke === '' ? 'type key' : props.isKeyUsed(keystroke) ? 'key in use' : 'assign';
  return (
    <tr>
      <th>
        <input size={3} maxLength={1} value={keystroke} spellCheck={false}
          onChange={(e) => changeKeystroke(e.target.value)} id="symbols" />
      </th>
      <td className='alternate'>
        <input size={entryWidth} spellCheck={false} value={symbols} onChange={(e) => changeSymbols(e.target.value)} />
        &nbsp;
        <button onClick={() => addAssignment()} disabled={!canUse}>{assign}</button>
      </td>
    </tr>
  );
}
